import { runInAction } from "mobx";

import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { AppointmentTypeCode } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import {
  EncounterLocation,
  EncounterStatus,
  EncounterType
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { catchNotFoundError } from "@libs/utils/utils.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";

/**
 * The purpose of this class is to load a clinical record before handing it over to PatientRecordScreenHelper.
 * By loading it in this component, PatientRecordScreen and every component that uses it can be assured
 *  that the clinical record has been loaded.
 */
export class PatientRecordScreenLoader {
  patientId: string;
  encounterId: string | undefined;
  calendarEventId: string | undefined;

  constructor(
    private root: IRootStore,
    public data: {
      patientId: string;
      encounterId: string | undefined;
      calendarEventId: string | undefined;
    }
  ) {
    this.patientId = data.patientId;
    this.encounterId = data.encounterId;
    this.calendarEventId = data.calendarEventId;
  }

  get booking() {
    return this.root.booking;
  }

  get clinical() {
    return this.root.clinical;
  }

  get core() {
    return this.root.core;
  }

  loadClinicalRecord = async (): Promise<{
    clinicalRecord: ClinicalRecord;
    showEncounterExistsPrompt?: boolean;
  }> => {
    const { encounterId } = this;

    if (encounterId) {
      const encounter = await this.clinical
        .getEncounter(encounterId)
        .catch(catchNotFoundError);
      if (!encounter) {
        runInAction(() => {
          this.clinical.encountersDeletedElsewhere.push(encounterId);
        });
      }

      const clinicalRecord = await this.clinical.getRecord(this.patientId, {
        encounterId
      });

      return { clinicalRecord };
    }

    const isViewOnly = !!routes.records.recordView.match(
      this.root.routing.location.pathname
    );

    if (isViewOnly) {
      const clinicalRecord = await this.clinical.getRecord(this.patientId);
      return { clinicalRecord };
    }

    if (this.calendarEventId) {
      return this.loadEncounterByCalendarEventId(this.calendarEventId);
    }

    return this.lookupEncounters();
  };

  private loadEncounterByCalendarEventId = async (calendarEventId: string) => {
    const calendarEvent = await this.booking.getCalendarEvent(calendarEventId);
    await this.booking.loadCalendarEventExtensions([calendarEvent]);

    const openEncounters =
      calendarEvent.calendarEventExtension?.openEncounters?.filter(
        encounter => encounter.userId === this.core.userId
      );

    if (openEncounters?.length) {
      const encounters = await this.clinical.getEncounters({
        patientId: this.patientId,
        encounterIds: openEncounters.map(({ encounterId }) => encounterId)
      });

      if (encounters.length) {
        const sortedEncounters = Array.from(encounters).sort((a, b) =>
          compareDatesPredicate(a.startDateTime, b.startDateTime, true)
        );

        const encounter = sortedEncounters[0];

        const clinicalRecord = await this.clinical.getRecord(this.patientId, {
          encounterId: encounter.id
        });
        return { clinicalRecord };
      }
    }

    const episodeOfCareId = calendarEvent.episodeOfCareId;

    if (episodeOfCareId) {
      // I'm not sure whether this is needed or whether this method should go straight to this.addEncounter
      const encounterForCondition = await this.clinical.getEncounters({
        patientId: this.patientId,
        userIds: [this.core.userId],
        statuses: [EncounterStatus.Open, EncounterStatus.Committed],
        episodeOfCareIds: [episodeOfCareId]
      });

      const encounter = encounterForCondition[0];

      if (encounter) {
        const clinicalRecord = await this.clinical.getRecord(this.patientId, {
          encounterId: encounter.id
        });

        return { clinicalRecord, showEncounterExistsPrompt: true };
      }

      return this.addEncounter();
    }

    return this.lookupEncounters();
  };

  private lookupEncounters = async () => {
    const encounters = await this.clinical.getEncounters({
      patientId: this.patientId,
      userIds: [this.core.userId],
      statuses: [EncounterStatus.Open, EncounterStatus.Committed]
    });

    if (encounters && encounters.length > 0) {
      const sortedEncounters = encounters.sort((a, b) =>
        compareDatesPredicate(a.startDateTime, b.startDateTime, true)
      );

      const mostRecentEncounter = sortedEncounters[0];

      const clinicalRecord = await this.clinical.getRecord(this.patientId, {
        encounterId: mostRecentEncounter.id
      });

      return { clinicalRecord, showEncounterExistsPrompt: true };
    }

    return this.addEncounter();
  };

  private addEncounter = async () => {
    const businessRoleCode = this.core.primaryBusinessRole?.code ?? "";

    const calendarEvent = this.calendarEventId
      ? await this.booking.getCalendarEvent(this.calendarEventId)
      : undefined;

    const episodeOfCareId = calendarEvent?.episodeOfCareId ?? undefined;

    const appointmentType = await calendarEvent?.loadAppointmentType();

    const isRecordUpdate = !!this.root.routing.match(
      routes.records.recordUpdate
    );

    const encounterType =
      isRecordUpdate ||
      appointmentType?.code === AppointmentTypeCode.RecordUpdate
        ? EncounterType.RecordUpdate
        : EncounterType.Consultation;

    const orgUnitId = await this.getLocationId(calendarEvent);

    const defaultValues = {
      encounterType,
      encounterLocation: EncounterLocation.Practice,
      orgUnitId,
      patientId: this.patientId,
      userId: this.core.userId,
      businessRole: businessRoleCode,
      episodeOfCareId
    };

    const encounter = await this.clinical.addEncounter({
      ...defaultValues
    });

    if (calendarEvent) {
      await this.booking.addAppointmentEncounters({
        calendarEventId: calendarEvent.id,
        encounterId: encounter.id,
        providerId: encounter.userId,
        businessRole: encounter.businessRole,
        visitDateTime: encounter.visitDateTime
      });
    }

    const clinicalRecord = await this.clinical.getRecord(this.patientId, {
      encounterId: encounter.id
    });

    await clinicalRecord.addAppointmentTypeInvoice(encounter);

    return { clinicalRecord };
  };

  private getLocationId = async (calendarEvent: CalendarEvent | undefined) => {
    if (!this.core.hasMultipleActiveLocations) {
      return this.core.locationId;
    } else if (calendarEvent?.orgUnitId) {
      return calendarEvent?.orgUnitId;
    }

    const orgUnitIdByCurrentTime = await this.getOrgUnitIdByCurrentTime();
    if (orgUnitIdByCurrentTime) return orgUnitIdByCurrentTime;

    const userAvailability = await this.booking.getUserAvailability(
      this.core.userId
    );

    return userAvailability.availableOrgUnitIds.length === 1
      ? userAvailability.availableOrgUnitIds[0]
      : this.core.locationId;
  };

  private getOrgUnitIdByCurrentTime = async (): Promise<string | undefined> => {
    const currentTime = DateTime.now();
    const from = currentTime.startOf("day");
    const to = from.plus({ day: 1 });
    const workingHours = await this.booking.getUsersWorkingHours({
      from,
      to,
      userIds: [this.core.userId],
      isStandardHours: false
    });

    const userWorkingHours = workingHours.find(
      u => u.userId === this.core.userId
    );

    const timeRanges = userWorkingHours?.orgUnitRanges.timeRanges ?? [];
    return timeRanges.find(
      timeRange => currentTime >= timeRange.from && currentTime < timeRange.to
    )?.orgUnitId;
  };
}
